import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { StatusBadge } from "../components/StatusBadge";
import { TierBadge, formatScore } from "../components/TierBadge";
import { Plus, UploadSimple, ClipboardText, Tag, Trash, DownloadSimple, X, CaretLeft, CaretRight, Sparkle, LinkedinLogo, Globe, Columns, Warning, Check } from "@phosphor-icons/react";
import { toast } from "sonner";
import { ImportDialog } from "../components/ImportDialog";

const PAGE_SIZE = 50;

const ALL_COLS = [
  { k: "name", label: "Name" },
  { k: "email", label: "Email" },
  { k: "company", label: "Company" },
  { k: "job_title", label: "Job title" },
  { k: "role_level", label: "Role / Level" },
  { k: "country", label: "Country" },
  { k: "linkedin", label: "LinkedIn" },
  { k: "website", label: "Website" },
  { k: "tags", label: "Tags" },
  { k: "stage", label: "Stage" },
  { k: "status", label: "Status" },
  { k: "score", label: "Score" },
  { k: "opens", label: "Opens" },
  { k: "created", label: "Created" },
];
const DEFAULT_COLS = ["name", "email", "company", "job_title", "role_level", "country", "tags", "status", "score"];
const COLS_KEY = "medihub_contact_cols";

const tagPart = (c, prefix) => (c.tags || []).filter(t => typeof t === "string" && t.startsWith(prefix)).map(t => t.slice(prefix.length));

export default function ContactsPage() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState(params.get("q") || "");
  const [tag, setTag] = useState("");
  const [stage, setStage] = useState("");
  const [status, setStatus] = useState("");
  const [companyFilter, setCompanyFilter] = useState(params.get("company") || "");
  const [tier, setTier] = useState(params.get("tier") || "");
  const [role, setRole] = useState(params.get("role") || "");
  const [level, setLevel] = useState(params.get("level") || "");
  const [org, setOrg] = useState(params.get("org") || "");
  const [jobTitle, setJobTitle] = useState("");
  const [hasLinkedin, setHasLinkedin] = useState(params.get("has_linkedin") || "");
  const [country, setCountry] = useState(params.get("country") || "");
  const [opts, setOpts] = useState({ roles: [], levels: [], orgs: [] });
  const [tagOpts, setTagOpts] = useState([]);
  const [openCols, setOpenCols] = useState(false);
  const [cols, setCols] = useState(() => {
    try {
      const s = JSON.parse(localStorage.getItem(COLS_KEY));
      if (Array.isArray(s) && s.length) return new Set(s);
    } catch { /* fall through */ }
    return new Set(DEFAULT_COLS);
  });
  const [sort, setSort] = useState("-created_at");
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [openAdd, setOpenAdd] = useState(false);
  const [openPaste, setOpenPaste] = useState(false);
  const [openCsv, setOpenCsv] = useState(false);
  const [scoring, setScoring] = useState(false);

  const load = async (p = page) => {
    setLoading(true);
    try {
      const data = await api.listContacts({ q, tag, stage, status, company: companyFilter, tier, role, level, org, job_title: jobTitle, has_linkedin: hasLinkedin, country, sort, page: p, page_size: PAGE_SIZE });
      setItems(data.items);
      setTotal(data.total);
      setPage(p);
      setSelected(new Set());
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(1); /* eslint-disable-next-line */ }, [q, tag, stage, status, companyFilter, tier, role, level, org, jobTitle, hasLinkedin, country, sort]);
  useEffect(() => { api.contactFilterOptions().then(setOpts).catch(() => {}); }, []);
  useEffect(() => { api.listTags().then(d => setTagOpts(d.items || [])).catch(() => {}); }, []);
  useEffect(() => {
    const v = params.get("q"); if (v !== null) setQ(v);
    const co = params.get("company"); if (co !== null) setCompanyFilter(co);
    const tr = params.get("tier"); if (tr !== null) setTier(tr);
    /* eslint-disable-next-line */
  }, [params]);

  const toggle = (id) => {
    const n = new Set(selected); n.has(id) ? n.delete(id) : n.add(id); setSelected(n);
  };
  const toggleAll = () => {
    if (selected.size === items.length) setSelected(new Set());
    else setSelected(new Set(items.map(i => i.id)));
  };

  const reviewLinkedin = async (id, action) => {
    try {
      await api.linkedinReview(id, action);
      toast.success(action === "confirm" ? "Marked as correct" : "Link removed — it can be re-enriched later");
      load(page);
    } catch {
      toast.error("Could not update");
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const saveCols = (next) => {
    setCols(next);
    localStorage.setItem(COLS_KEY, JSON.stringify(Array.from(next)));
  };
  const toggleCol = (k) => {
    const n = new Set(cols);
    n.has(k) ? n.delete(k) : n.add(k);
    if (n.size === 0) return;
    saveCols(n);
  };
  const show = (k) => cols.has(k) || (k === "linkedin" && hasLinkedin === "review");
  const visibleCount = ALL_COLS.filter(c => show(c.k)).length;

  const exportUrl = useMemo(() => {
    const p = new URLSearchParams();
    const pairs = { q, tag, stage, status, company: companyFilter, tier, role, level, org, job_title: jobTitle, has_linkedin: hasLinkedin, country };
    Object.entries(pairs).forEach(([k, v]) => { if (v) p.set(k, v); });
    const qs = p.toString();
    return `${process.env.REACT_APP_BACKEND_URL}/api/contacts/export${qs ? `?${qs}` : ""}`;
  }, [q, tag, stage, status, companyFilter, tier, role, level, org, jobTitle, hasLinkedin, country]);

  const anyFilter = q || tag || stage || status || companyFilter || tier || role || level || org || jobTitle || hasLinkedin || country;
  const clearAll = () => {
    setQ(""); setTag(""); setStage(""); setStatus(""); setCompanyFilter(""); setTier("");
    setRole(""); setLevel(""); setOrg(""); setJobTitle(""); setHasLinkedin(""); setCountry("");
    setParams({});
  };

  const doBulk = async (action, value) => {
    if (selected.size === 0) return;
    if (action === "delete" && !window.confirm(`Delete ${selected.size} contacts?`)) return;
    const res = await api.bulkAction({ contact_ids: Array.from(selected), action, value });
    toast.success(`${action} · ${res.affected} contacts`);
    load(page);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="font-heading text-2xl font-medium tracking-tight text-zinc-950">Contacts</h2>
          <p className="text-sm text-zinc-500 mt-0.5">{total.toLocaleString()} total</p>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="btn-recompute-score" onClick={async () => { setScoring(true); try { const r = await api.recomputeEngagement(); toast.success(`${r.contacts_scored} contacts re-scored`); load(page); } finally { setScoring(false); } }} className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md border border-zinc-200 hover:bg-zinc-50" disabled={scoring}>
            <Sparkle size={14}/> {scoring ? "Scoring…" : "Recompute scores"}
          </button>
          <button data-testid="btn-paste-bulk" onClick={() => setOpenPaste(true)} className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md border border-zinc-200 hover:bg-zinc-50">
            <ClipboardText size={14}/> Bulk Paste
          </button>
          <button data-testid="btn-import-csv" onClick={() => setOpenCsv(true)} title="Upload a CSV or Excel export (e.g. Hunter.io) — adds new contacts and updates existing ones" className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md border border-zinc-200 hover:bg-zinc-50">
            <UploadSimple size={14}/> Import
          </button>
          <a data-testid="btn-export-csv" href={exportUrl} className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md border border-zinc-200 hover:bg-zinc-50" title={anyFilter ? "Exports the current filtered view" : "Exports all contacts"}>
            <DownloadSimple size={14}/> Export{anyFilter ? " (filtered)" : ""}
          </a>
          <button data-testid="btn-new-contact" onClick={() => setOpenAdd(true)} className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md bg-zinc-950 text-white hover:bg-zinc-800">
            <Plus size={14}/> New contact
          </button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 items-center rounded-lg border border-zinc-200 bg-white p-3">
        <input data-testid="contacts-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email, company…" className="h-8 flex-1 min-w-[240px] rounded-md border border-zinc-200 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-zinc-950" />
        <select data-testid="contacts-stage" value={stage} onChange={(e) => setStage(e.target.value)} className="h-8 rounded-md border border-zinc-200 px-2 text-sm">
          <option value="">All stages</option>
          <option value="lead">Lead</option>
          <option value="qualified">Qualified</option>
          <option value="opportunity">Opportunity</option>
          <option value="customer">Customer</option>
        </select>
        <select data-testid="contacts-status" value={status} onChange={(e) => setStatus(e.target.value)} className="h-8 rounded-md border border-zinc-200 px-2 text-sm">
          <option value="">All statuses</option>
          <option value="active">Active</option>
          <option value="left_company">No longer at company</option>
          <option value="unsubscribed">Unsubscribed</option>
          <option value="bounced">Bounced</option>
        </select>
        <select data-testid="contacts-tier" value={tier} onChange={(e) => setTier(e.target.value)} className="h-8 rounded-md border border-zinc-200 px-2 text-sm">
          <option value="">All tiers</option>
          <option value="hot">🔥 Hot (15+)</option>
          <option value="warm">Warm (5–15)</option>
          <option value="cool">Cool (0–5)</option>
          <option value="cold">Cold (0)</option>
        </select>
        <input data-testid="contacts-company-filter" value={companyFilter} onChange={(e) => setCompanyFilter(e.target.value)} placeholder="Company…" className="h-8 w-40 rounded-md border border-zinc-200 px-2 text-sm" />
        <select data-testid="contacts-role-filter" value={role} onChange={(e) => setRole(e.target.value)} className="h-8 rounded-md border border-zinc-200 px-2 text-sm max-w-[180px]">
          <option value="">All roles</option>
          {opts.roles.map(r => <option key={r} value={r}>{r}</option>)}
        </select>
        <select data-testid="contacts-level-filter" value={level} onChange={(e) => setLevel(e.target.value)} className="h-8 rounded-md border border-zinc-200 px-2 text-sm">
          <option value="">All levels</option>
          {opts.levels.map(l => <option key={l} value={l}>{l}</option>)}
        </select>
        <select data-testid="contacts-org-filter" value={org} onChange={(e) => setOrg(e.target.value)} className="h-8 rounded-md border border-zinc-200 px-2 text-sm max-w-[160px]">
          <option value="">All org types</option>
          {opts.orgs.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
        <input data-testid="contacts-jobtitle-filter" value={jobTitle} onChange={(e) => setJobTitle(e.target.value)} placeholder="Job title contains… (comma = OR)" title="Separate multiple terms with commas, e.g. trial, clinical research" className="h-8 w-48 rounded-md border border-zinc-200 px-2 text-sm" />
        <select data-testid="contacts-haslinkedin-filter" value={hasLinkedin} onChange={(e) => setHasLinkedin(e.target.value)} className="h-8 rounded-md border border-zinc-200 px-2 text-sm">
          <option value="">LinkedIn: any</option>
          <option value="yes">Has LinkedIn</option>
          <option value="no">No LinkedIn</option>
          <option value="review">LinkedIn: needs review</option>
        </select>
        <select data-testid="contacts-country-filter" value={country} onChange={(e) => setCountry(e.target.value)} className="h-8 max-w-[160px] rounded-md border border-zinc-200 px-2 text-sm">
          <option value="">All countries</option>
          {(opts.countries || []).map(c => <option key={c} value={c}>{c}</option>)}
          <option value="unknown">Country unknown</option>
        </select>
        <select data-testid="contacts-tag-filter" value={tag} onChange={(e) => setTag(e.target.value)} className="h-8 max-w-[160px] rounded-md border border-zinc-200 px-2 text-sm">
          <option value="">All tags</option>
          {tagOpts.map(t => <option key={t.name} value={t.name}>{t.name}{t.count ? ` (${t.count})` : ""}</option>)}
          {tag && !tagOpts.some(t => t.name === tag) && <option value={tag}>{tag}</option>}
        </select>
        {anyFilter && (
          <button onClick={clearAll} className="text-xs text-zinc-500 hover:text-zinc-900 inline-flex items-center gap-1"><X size={12}/> Clear all</button>
        )}
        <div className="relative ml-auto">
          <button data-testid="btn-columns" onClick={() => setOpenCols(v => !v)} className="h-8 inline-flex items-center gap-1.5 text-sm px-3 rounded-md border border-zinc-200 hover:bg-zinc-50">
            <Columns size={14}/> Columns
          </button>
          {openCols && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setOpenCols(false)} />
              <div className="absolute right-0 top-9 z-20 w-52 rounded-lg border border-zinc-200 bg-white shadow-lg p-2" data-testid="columns-menu">
                {ALL_COLS.map(col => (
                  <label key={col.k} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-zinc-50 cursor-pointer text-sm">
                    <input type="checkbox" data-testid={`col-toggle-${col.k}`} checked={cols.has(col.k)} onChange={() => toggleCol(col.k)} />
                    {col.label}
                  </label>
                ))}
                <button data-testid="columns-reset" onClick={() => saveCols(new Set(DEFAULT_COLS))} className="w-full text-left px-2 py-1.5 text-xs text-zinc-500 hover:text-zinc-900 border-t border-zinc-100 mt-1 pt-2">Reset to defaults</button>
              </div>
            </>
          )}
        </div>
      </div>

      {selected.size > 0 && (
        <div className="flex items-center gap-2 rounded-lg border border-zinc-300 bg-zinc-50 px-3 py-2" data-testid="bulk-bar">
          <span className="text-sm font-medium">{selected.size} selected</span>
          <span className="flex-1" />
          <input id="add-tag-val" list="tag-suggestions" placeholder="tag name" className="h-7 rounded-md border border-zinc-200 bg-white px-2 text-xs w-28" />
          <datalist id="tag-suggestions">{tagOpts.map(t => <option key={t.name} value={t.name} />)}</datalist>
          <button data-testid="bulk-add-tag" onClick={() => doBulk("add_tag", document.getElementById("add-tag-val").value)} className="text-xs px-2 py-1 rounded-md border border-zinc-200 bg-white hover:bg-zinc-100"><Tag size={12} className="inline mr-1"/>Add tag</button>
          <button data-testid="bulk-left-company" onClick={() => doBulk("left_company")} title="Mark as no longer at their company — excluded from all sends until reactivated" className="text-xs px-2 py-1 rounded-md border border-orange-200 text-orange-700 bg-white hover:bg-orange-50">Left company</button>
          <button data-testid="bulk-unsubscribe" onClick={() => doBulk("unsubscribe")} className="text-xs px-2 py-1 rounded-md border border-zinc-200 bg-white hover:bg-zinc-100">Unsubscribe</button>
          <button data-testid="bulk-delete" onClick={() => doBulk("delete")} className="text-xs px-2 py-1 rounded-md border border-rose-200 text-rose-700 bg-white hover:bg-rose-50"><Trash size={12} className="inline mr-1"/>Delete</button>
        </div>
      )}

      <div className="rounded-lg border border-zinc-200 bg-white overflow-hidden">
        <div className="max-h-[calc(100vh-340px)] overflow-auto">
          <table className="w-full text-sm">
            <thead className="sticky-thead text-xs text-zinc-500 border-b border-zinc-200">
              <tr>
                <th className="text-left px-3 py-2 w-8"><input type="checkbox" checked={selected.size === items.length && items.length > 0} onChange={toggleAll} data-testid="select-all"/></th>
                {show("name") && <th className="text-left px-3 py-2 font-medium">Name</th>}
                {show("email") && <th className="text-left px-3 py-2 font-medium">Email</th>}
                {show("company") && <th className="text-left px-3 py-2 font-medium">Company</th>}
                {show("job_title") && <th className="text-left px-3 py-2 font-medium">Job title</th>}
                {show("role_level") && <th className="text-left px-3 py-2 font-medium">Role / Level</th>}
                {show("country") && <th className="text-left px-3 py-2 font-medium">Country</th>}
                {show("linkedin") && <th className="text-left px-3 py-2 font-medium">LinkedIn</th>}
                {show("website") && <th className="text-left px-3 py-2 font-medium">Website</th>}
                {show("tags") && <th className="text-left px-3 py-2 font-medium">Tags</th>}
                {show("stage") && <th className="text-left px-3 py-2 font-medium">Stage</th>}
                {show("status") && <th className="text-left px-3 py-2 font-medium">Status</th>}
                {show("score") && (
                  <th className="text-right px-3 py-2 font-medium">
                    <button onClick={() => setSort(sort === "-engagement_score" ? "engagement_score" : "-engagement_score")} className="hover:text-zinc-950 inline-flex items-center gap-1" data-testid="sort-score">
                      Score {sort === "-engagement_score" ? "↓" : sort === "engagement_score" ? "↑" : ""}
                    </button>
                  </th>
                )}
                {show("opens") && <th className="text-right px-3 py-2 font-medium">Opens</th>}
                {show("created") && <th className="text-left px-3 py-2 font-medium">Created</th>}
              </tr>
            </thead>
            <tbody>
              {items.map(c => {
                const roles = tagPart(c, "role: ");
                const levels = tagPart(c, "level: ");
                const otherTags = (c.tags || []).filter(t => typeof t === "string" && !t.startsWith("role: ") && !t.startsWith("level: "));
                return (
                <tr key={c.id} className="border-b border-zinc-100 hover:bg-zinc-50/60 cursor-pointer" data-testid={`row-${c.email}`} onClick={() => navigate(`/contacts/${c.id}`)}>
                  <td className="px-3 py-2.5 align-middle" onClick={(e) => e.stopPropagation()}>
                    <input type="checkbox" checked={selected.has(c.id)} onChange={() => toggle(c.id)} />
                  </td>
                  {show("name") && <td className="px-3 py-2.5 font-medium text-zinc-900 whitespace-nowrap">{c.first_name} {c.last_name}</td>}
                  {show("email") && <td className="px-3 py-2.5 text-zinc-700 font-mono text-[12.5px]">{c.email}</td>}
                  {show("company") && (
                    <td className="px-3 py-2.5 text-zinc-700 max-w-[180px] truncate" onClick={(e) => e.stopPropagation()}>
                      {c.company ? (
                        <button data-testid={`filter-company-${c.email}`} onClick={() => setCompanyFilter(c.company)} title={`Filter by ${c.company}`} className="hover:underline hover:text-zinc-950 text-left truncate max-w-full">{c.company}</button>
                      ) : <span className="text-zinc-300">—</span>}
                    </td>
                  )}
                  {show("job_title") && (
                    <td className="px-3 py-2.5 text-zinc-700 max-w-[220px] truncate" onClick={(e) => e.stopPropagation()}>
                      {c.job_title ? (
                        <button data-testid={`filter-title-${c.email}`} onClick={() => setJobTitle(c.job_title)} title={`Filter by "${c.job_title}"`} className="hover:underline hover:text-zinc-950 text-left truncate max-w-full">{c.job_title}</button>
                      ) : <span className="text-zinc-300">—</span>}
                    </td>
                  )}
                  {show("role_level") && (
                    <td className="px-3 py-2.5" onClick={(e) => e.stopPropagation()}>
                      <div className="flex flex-wrap gap-1">
                        {roles.map(r => <button key={r} onClick={() => setRole(r)} title={`Filter by role: ${r}`} className="text-[10px] px-1.5 py-0.5 rounded bg-sky-50 text-sky-800 border border-sky-100 hover:bg-sky-100 cursor-pointer">{r}</button>)}
                        {levels.map(l => <button key={l} onClick={() => setLevel(l)} title={`Filter by level: ${l}`} className="text-[10px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-800 border border-amber-100 hover:bg-amber-100 cursor-pointer">{l}</button>)}
                        {roles.length === 0 && levels.length === 0 && <span className="text-zinc-300">—</span>}
                      </div>
                    </td>
                  )}
                  {show("country") && (
                    <td className="px-3 py-2.5 text-zinc-700 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                      {c.country ? (
                        <button data-testid={`filter-country-${c.email}`} onClick={() => setCountry(c.country)} title={`Filter by ${c.country}`} className="hover:underline hover:text-zinc-950">{c.country}</button>
                      ) : <span className="text-zinc-300">—</span>}
                    </td>
                  )}
                  {show("linkedin") && (
                    <td className="px-3 py-2.5" onClick={(e) => e.stopPropagation()}>
                      {c.linkedin_url ? (
                        <span className="inline-flex items-center gap-1">
                          <a href={c.linkedin_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sky-700 hover:text-sky-900" data-testid={`linkedin-${c.email}`}>
                            <LinkedinLogo size={16} weight="fill"/>
                          </a>
                          {c.linkedin_flag === "review" && (
                            <span className="inline-flex items-center gap-0.5">
                              <span title="This person's name doesn't appear in the LinkedIn URL — worth checking it's the right profile" data-testid={`linkedin-review-${c.email}`}>
                                <Warning size={13} weight="fill" className="text-amber-500"/>
                              </span>
                              <button data-testid={`linkedin-confirm-${c.email}`} title="Confirm it's the right person — clears the flag" onClick={() => reviewLinkedin(c.id, "confirm")} className="p-0.5 rounded text-emerald-600 hover:bg-emerald-100">
                                <Check size={13} weight="bold"/>
                              </button>
                              <button data-testid={`linkedin-wrong-${c.email}`} title="Wrong profile — removes the link so it can be re-enriched" onClick={() => reviewLinkedin(c.id, "wrong")} className="p-0.5 rounded text-rose-600 hover:bg-rose-100">
                                <X size={13} weight="bold"/>
                              </button>
                            </span>
                          )}
                        </span>
                      ) : <span className="text-zinc-300">—</span>}
                    </td>
                  )}
                  {show("website") && (
                    <td className="px-3 py-2.5" onClick={(e) => e.stopPropagation()}>
                      {c.website ? (
                        <a href={/^https?:\/\//.test(c.website) ? c.website : `https://${c.website}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-zinc-600 hover:text-zinc-950" data-testid={`website-${c.email}`}>
                          <Globe size={16}/>
                        </a>
                      ) : <span className="text-zinc-300">—</span>}
                    </td>
                  )}
                  {show("tags") && (
                    <td className="px-3 py-2.5" onClick={(e) => e.stopPropagation()}>
                      <div className="flex flex-wrap gap-1">
                        {otherTags.slice(0, 3).map(t => <button key={t} onClick={() => setTag(t)} title={`Filter by tag: ${t}`} className="text-[10px] px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-700 hover:bg-zinc-200 cursor-pointer">{t}</button>)}
                        {otherTags.length > 3 && <span className="text-[10px] px-1.5 py-0.5 rounded bg-zinc-50 text-zinc-400">+{otherTags.length - 3}</span>}
                        {otherTags.length === 0 && <span className="text-zinc-300 text-xs">—</span>}
                      </div>
                    </td>
                  )}
                  {show("stage") && <td className="px-3 py-2.5"><StatusBadge value={c.lifecycle_stage}/></td>}
                  {show("status") && <td className="px-3 py-2.5"><StatusBadge value={c.status}/></td>}
                  {show("score") && (
                    <td className="px-3 py-2.5 text-right">
                      <span className="inline-flex items-center gap-1.5">
                        <TierBadge score={c.engagement_score || 0} />
                        <ScoreCell value={c.engagement_score || 0} />
                      </span>
                    </td>
                  )}
                  {show("opens") && <td className="px-3 py-2.5 text-right text-zinc-700">{c.total_opens || 0}</td>}
                  {show("created") && <td className="px-3 py-2.5 text-zinc-500 whitespace-nowrap text-xs">{c.created_at ? new Date(c.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "—"}</td>}
                </tr>
                );
              })}
              {items.length === 0 && !loading && (
                <tr><td colSpan={visibleCount + 1} className="p-8 text-center text-sm text-zinc-500">No contacts found</td></tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between px-3 py-2 border-t border-zinc-200 text-xs text-zinc-600">
          <span>Page {page} of {totalPages}</span>
          <div className="flex items-center gap-1">
            <button data-testid="page-prev" disabled={page <= 1} onClick={() => load(page-1)} className="px-2 py-1 rounded border border-zinc-200 disabled:opacity-40"><CaretLeft size={12}/></button>
            <button data-testid="page-next" disabled={page >= totalPages} onClick={() => load(page+1)} className="px-2 py-1 rounded border border-zinc-200 disabled:opacity-40"><CaretRight size={12}/></button>
          </div>
        </div>
      </div>

      {openAdd && <NewContactModal onClose={() => setOpenAdd(false)} onSaved={() => { setOpenAdd(false); load(1); }} />}
      {openPaste && <BulkPasteModal onClose={() => setOpenPaste(false)} onDone={() => { setOpenPaste(false); load(1); }} />}
      {openCsv && <ImportDialog onClose={() => setOpenCsv(false)} onDone={() => { setOpenCsv(false); load(1); }} />}
    </div>
  );
}

function NewContactModal({ onClose, onSaved }) {
  const [f, setF] = useState({ email: "", first_name: "", last_name: "", company: "", job_title: "", website: "", linkedin_url: "", lifecycle_stage: "lead" });
  const [busy, setBusy] = useState(false);
  const canSave = f.email.trim() && f.first_name.trim() && f.last_name.trim() && f.company.trim();
  const submit = async () => {
    setBusy(true);
    try { await api.createContact(f); toast.success("Contact added"); onSaved(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <Modal title="New contact" subtitle="Email, first name, last name and company are compulsory — the rest is strongly encouraged." onClose={onClose}>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Email *"><input data-testid="nc-email" value={f.email} onChange={(e) => setF({...f, email: e.target.value})} className="modal-input"/></Field>
        <Field label="Company *"><input data-testid="nc-company" value={f.company} onChange={(e) => setF({...f, company: e.target.value})} className="modal-input"/></Field>
        <Field label="First name *"><input data-testid="nc-first" value={f.first_name} onChange={(e) => setF({...f, first_name: e.target.value})} className="modal-input"/></Field>
        <Field label="Last name *"><input data-testid="nc-last" value={f.last_name} onChange={(e) => setF({...f, last_name: e.target.value})} className="modal-input"/></Field>
        <Field label="Job title"><input data-testid="nc-title" value={f.job_title} onChange={(e) => setF({...f, job_title: e.target.value})} className="modal-input"/></Field>
        <Field label="Stage">
          <select data-testid="nc-stage" value={f.lifecycle_stage} onChange={(e) => setF({...f, lifecycle_stage: e.target.value})} className="modal-input">
            <option value="lead">Lead</option><option value="qualified">Qualified</option><option value="opportunity">Opportunity</option><option value="customer">Customer</option>
          </select>
        </Field>
        <Field label="Website"><input data-testid="nc-website" value={f.website} onChange={(e) => setF({...f, website: e.target.value})} className="modal-input" placeholder="acme.com"/></Field>
        <Field label="LinkedIn URL"><input data-testid="nc-linkedin" value={f.linkedin_url} onChange={(e) => setF({...f, linkedin_url: e.target.value})} className="modal-input" placeholder="https://linkedin.com/in/…"/></Field>
      </div>
      <ModalFooter onClose={onClose}>
        <button data-testid="nc-save" disabled={busy || !canSave} onClick={submit} className="modal-btn-primary">Save</button>
      </ModalFooter>
    </Modal>
  );
}

function BulkPasteModal({ onClose, onDone }) {
  const [text, setText] = useState("");
  const [tags, setTags] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      const r = await api.bulkPaste({ text, default_tags: tags.split(",").map(t=>t.trim()).filter(Boolean) });
      toast.success(`${r.created} added · ${r.skipped_duplicates} skipped · ${r.error_count} errors`);
      onDone();
    } finally { setBusy(false); }
  };
  return (
    <Modal title="Bulk paste contacts" subtitle="One per line. Formats: email · or · email | first | last | company · or · email, first, last, company" onClose={onClose} wide>
      <textarea data-testid="paste-textarea" value={text} onChange={e=>setText(e.target.value)} rows={12} className="w-full rounded-md border border-zinc-200 p-3 text-sm font-mono" placeholder="alice@acme.com&#10;bob@globex.com | Bob | Smith | Globex&#10;carol@initech.com, Carol, Lee, Initech"/>
      <div className="mt-2"><Field label="Default tags (comma separated)"><input data-testid="paste-tags" value={tags} onChange={e=>setTags(e.target.value)} className="modal-input" placeholder="newsletter, q1-import"/></Field></div>
      <ModalFooter onClose={onClose}>
        <button data-testid="paste-submit" disabled={busy || !text.trim()} onClick={submit} className="modal-btn-primary">Add contacts</button>
      </ModalFooter>
    </Modal>
  );
}

export function Modal({ title, subtitle, onClose, children, wide }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="modal-overlay">
      <div onClick={(e) => e.stopPropagation()} className={`bg-white rounded-lg border border-zinc-200 shadow-xl w-full ${wide ? "max-w-2xl" : "max-w-lg"} max-h-[90vh] overflow-auto`}>
        <div className="px-5 py-4 border-b border-zinc-200 flex items-start justify-between gap-3">
          <div>
            <h3 className="font-heading text-base font-medium">{title}</h3>
            {subtitle && <p className="text-xs text-zinc-500 mt-0.5">{subtitle}</p>}
          </div>
          <button onClick={onClose} className="text-zinc-400 hover:text-zinc-900" data-testid="modal-close"><X size={16}/></button>
        </div>
        <div className="p-5">{children}</div>
      </div>
      <style>{`.modal-input{display:block;width:100%;height:36px;border-radius:6px;border:1px solid #e4e4e7;padding:0 10px;font-size:13px;background:white}
      .modal-input:focus{outline:none;box-shadow:0 0 0 1px #09090b;border-color:#09090b}
      .modal-btn-primary{padding:8px 14px;font-size:13px;font-weight:500;background:#09090b;color:white;border-radius:6px}
      .modal-btn-primary:hover{background:#27272a}.modal-btn-primary:disabled{opacity:.5;cursor:not-allowed}`}</style>
    </div>
  );
}

export function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[11px] font-medium text-zinc-600 uppercase tracking-wider mb-1">{label}</span>
      {children}
    </label>
  );
}

export function ModalFooter({ onClose, children }) {
  return (
    <div className="mt-5 flex items-center justify-end gap-2">
      <button onClick={onClose} className="px-3 py-1.5 text-sm rounded-md border border-zinc-200 hover:bg-zinc-50">Cancel</button>
      {children}
    </div>
  );
}

export function ScoreCell({ value }) {
  const v = Number(value) || 0;
  const tone = v >= 5 ? "text-emerald-700 bg-emerald-50 border-emerald-200"
             : v >= 2 ? "text-blue-700 bg-blue-50 border-blue-200"
             : v >= 0.5 ? "text-zinc-700 bg-zinc-100 border-zinc-200"
             : "text-zinc-400 bg-transparent border-transparent";
  return (
    <span className={`inline-flex items-center justify-center min-w-[2.25rem] text-[11px] font-medium tabular-nums rounded border px-1.5 py-0.5 ${tone}`} title="Lead score: opens, clicks and publication requests weighted by recency">
      {formatScore(v)}
    </span>
  );
}

